/**
 * ATTEST-EVIDENCE-REQUIRED-1 (items 2 and 3) — the Step-3 checklist.
 *
 * Presentation only. Every status it draws comes from `buildChecklist`, and it
 * adds no rule of its own.
 *
 * A row says one of two different things depending on whether the engine has
 * spoken yet, and the difference is the whole point of item 3:
 *
 * - **Before a build** the word is "attached". The CPA has put a document of an
 *   accepted kind against the statement; nothing has been matched to anything.
 *   Saying "verified" there would be the form vouching for a check it has not
 *   run, on the one screen whose purpose is that the letter's claims trace to
 *   something.
 * - **After a build** the word is "verified", "needs review", or the specific
 *   thing that did not line up — and every one of those sentences is the
 *   server's own `reason`, rendered verbatim. This component never composes a
 *   failure sentence, because composing one means deciding what failed, and
 *   entity/period/figure matching needs the document, which is not here.
 *
 * `per_period` rows render once. Expanding them into one row per year would
 * mean the client deciding what the periods in scope are, and
 * `core/attest/period_scope.py` owns that; the row says so in words instead.
 */
import type { ChecklistRow, ChecklistSummary } from '@/components/attest/documentChecklist'

interface Mark {
  glyph: string
  className: string
}

/** The mark, and what it means. `not_used` gets no box — nothing is being asked. */
const MARKS: Record<ChecklistRow['status'], Mark> = {
  satisfied: { glyph: '☑', className: 'text-emerald-600' },
  attested: { glyph: '☑', className: 'text-emerald-600' },
  mismatch: { glyph: '☐', className: 'text-amber-600' },
  missing: { glyph: '☐', className: 'text-muted-foreground' },
  not_used: { glyph: '·', className: 'text-muted-foreground' },
}

/** The engine's verdict outranks the form's own description of itself. */
const VERIFICATION_MARKS: Record<'verified' | 'failed' | 'review', Mark> = {
  verified: { glyph: '☑', className: 'text-emerald-600' },
  failed: { glyph: '✕', className: 'text-destructive' },
  review: { glyph: '!', className: 'text-amber-600' },
}

function markFor(row: ChecklistRow): Mark {
  return row.verification ? VERIFICATION_MARKS[row.verification.state] : MARKS[row.status]
}

function statusNote(row: ChecklistRow): string {
  switch (row.status) {
    case 'satisfied':
      return `Attached: ${row.matchedFilenames.join(', ')}`
    case 'attested':
      return 'Attested as supported by records in your file — no document attached.'
    case 'mismatch':
      // Advisory. The classifier read the upload as something else; it may be
      // wrong, and the server re-reads the file regardless. So this tells the
      // CPA what was read, and does not tell them they are wrong.
      return 'A document is attached, but it was read as a different kind. Check it is the right file.'
    case 'not_used':
      return 'No statement draws on this yet.'
    default:
      return ''
  }
}

/**
 * What the engine said, or `[]` before it has said anything.
 *
 * A verified row gets one line naming the files that held up. Anything else
 * gets the findings' own prose — one line each, because two documents can fail
 * for two different reasons and collapsing them would leave the CPA re-opening
 * both to find out which.
 */
function verificationNotes(row: ChecklistRow): string[] {
  const verdict = row.verification
  if (!verdict) return []
  if (verdict.state === 'verified') {
    return row.matchedFilenames.length
      ? [`Verified against ${row.matchedFilenames.join(', ')}.`]
      : ['Verified.']
  }
  const reasons = verdict.findings.map(f => f.reason).filter(Boolean)
  // The engine does not go quiet on a failure, but if it ever did, a row that
  // said nothing would read as a pass. Say the state instead.
  if (!reasons.length) {
    return verdict.state === 'failed'
      ? ['This document does not support the statement it is attached to.']
      : ['This needs a human to look at it before the letter can be issued.']
  }
  return reasons
}

/**
 * Every line under the row's title.
 *
 * "Verified against X" already names the file, so the `Attached: X` line is
 * dropped once it holds — two lines naming the same document, one of which is
 * the weaker claim, reads as two separate facts.
 */
function notesFor(row: ChecklistRow): string[] {
  const verified = row.verification?.state === 'verified'
  const status = verified && row.status === 'satisfied' ? '' : statusNote(row)
  return [status, ...verificationNotes(row)].filter(Boolean)
}

export interface RequiredDocumentsChecklistProps {
  summary: ChecklistSummary
}

export function RequiredDocumentsChecklist({ summary }: RequiredDocumentsChecklistProps) {
  if (summary.rows.length === 0) return null

  return (
    <section className="rounded-lg border border-border p-4" data-testid="documents-checklist">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">Documents this letter needs</h3>
        <span
          className="text-sm text-muted-foreground"
          data-testid="documents-checklist-progress"
        >
          {summary.total === 0
            ? 'Pick an assertion below to see what to attach'
            : summary.verified === null
              ? `${summary.done} of ${summary.total} attached`
              // Only ever after a build. `verified` is `null`, not `0`, until
              // the engine has judged this exact form — so this line can never
              // claim a check that did not run.
              : `${summary.verified} of ${summary.total} verified`}
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Attach each one in the Evidence slot of the statement that draws on it.
        The engine re-reads what you attach — it does not take a typed citation.
      </p>

      <ul className="mt-3 space-y-2">
        {summary.rows.map(row => {
          const mark = markFor(row)
          const inPlay = row.status !== 'not_used'
          return (
            <li
              key={row.key}
              data-testid={`checklist-row-${row.key}`}
              data-status={row.status}
              data-verification={row.verification?.state ?? 'unchecked'}
              className={`flex gap-3 rounded-md border p-3 text-sm ${
                inPlay ? 'border-border' : 'border-dashed border-border/60 opacity-60'
              }`}
            >
              <span aria-hidden className={`text-base leading-5 ${mark.className}`}>
                {mark.glyph}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{row.label}</span>
                  {row.isRequired ? (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs">Required</span>
                  ) : (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs">Recommended</span>
                  )}
                  {row.perPeriod && (
                    <span className="text-xs text-muted-foreground">
                      One per tax year in scope
                    </span>
                  )}
                </div>
                {row.description && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{row.description}</p>
                )}
                {notesFor(row).map((note, i) => (
                  <p
                    key={`${row.key}-note-${i}`}
                    className={`mt-1 text-xs ${
                      row.verification?.state === 'failed'
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {note}
                  </p>
                ))}
                {inPlay && row.statementIndexes.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Drawn on by {row.statementIndexes.length === 1 ? 'statement' : 'statements'}{' '}
                    {row.statementIndexes.map(i => i + 1).join(', ')}.
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export interface DocumentsToGatherProps {
  /** The chosen type's rows, straight off the summary — no statements yet. */
  rows: Array<{ key: string; label: string; description: string; requirement: string }>
}

/**
 * The Step-1 surface: what to go and collect, shown as soon as a letter type is
 * chosen. No ticks and no progress — nothing has been attached yet, and a row
 * of empty boxes on the first screen reads as a list of failures rather than a
 * list of errands.
 */
export function DocumentsToGather({ rows }: DocumentsToGatherProps) {
  if (rows.length === 0) return null
  return (
    <section className="rounded-lg border border-border p-4" data-testid="documents-to-gather">
      <h3 className="text-sm font-semibold">Documents to gather</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        This letter type draws on the following. You will attach them in Step 3.
      </p>
      <ul className="mt-2 space-y-1 text-sm">
        {rows.map(row => (
          <li key={row.key} className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium">{row.label}</span>
            <span className="text-xs text-muted-foreground">
              {row.requirement === 'required' ? 'Required' : 'Recommended'}
              {row.description ? ` — ${row.description}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
